type LogChannel = 'acabado' | 'gola'

/** Logs de depuración linked gola/acabado (desactivado en producción). */
const LINKED_CHANGE_DEBUG_LOGS = false

const LOG_PREFIX: Record<LogChannel, string> = {
  acabado: '[linkedAcabado]',
  gola: '[linkedGola]',
}

let lastAcabadoModeEditor = ''

export function getLastAcabadoModeEditor(): string {
  return lastAcabadoModeEditor
}

export function setLastAcabadoModeEditor(modeEditor?: string) {
  if (typeof modeEditor === 'string' && modeEditor) lastAcabadoModeEditor = modeEditor
}

const SPECIAL_ACABADO_LABELS = new Set([
  'LARA',
  'LINEAL',
  'RUSTIK',
  'RUSTIK2',
  'RUSTIK 2',
  'LA',
  'LI',
  'RU',
  'R2',
])

let activeAcabadoTraceId: string | null = null
let activeGolaTraceId: string | null = null

function getTraceId(channel: LogChannel): string | null {
  return channel === 'gola' ? activeGolaTraceId : activeAcabadoTraceId
}

function summarizeApiResult(result: unknown): Record<string, unknown> | null {
  if (result == null) return null
  const data =
    typeof result === 'object' && result !== null && 'data' in result
      ? (result as { data: unknown }).data
      : result
  if (!data || typeof data !== 'object') return { raw: String(data) }
  const d = data as Record<string, unknown>
  return {
    codigo: d.codigo,
    idUnidades: d.idUnidades,
    referencia: d.referencia,
    referenciaBlender: d.referenciaBlender,
    idCambioArmazon: d.idCambioArmazon,
    idCambioGolaBlanco: d.idCambioGolaBlanco,
    idCambioGolaPetroleo: d.idCambioGolaPetroleo,
    idCambioRutik: d.idCambioRutik,
    idCambioRutik2: d.idCambioRutik2,
    idCambioLineal: d.idCambioLineal,
    idCambioLara: d.idCambioLara,
    acabadosCount: Array.isArray(d.acabados) ? d.acabados.length : undefined,
    elementosEnlazadosCount: Array.isArray(d.elementosEnlazados)
      ? d.elementosEnlazados.length
      : undefined,
  }
}

function logChannel(channel: LogChannel, step: string, data?: Record<string, unknown>) {
  if (!LINKED_CHANGE_DEBUG_LOGS) return
  const modeEditor =
    (typeof data?.modeEditor === 'string' && data.modeEditor) || lastAcabadoModeEditor || undefined
  if (channel === 'acabado' && typeof data?.modeEditor === 'string') {
    setLastAcabadoModeEditor(data.modeEditor)
  }
  console.log(`${LOG_PREFIX[channel]}[modeEditor=${modeEditor || '?'}]`, step, {
    traceId: getTraceId(channel),
    ts: new Date().toISOString(),
    modeEditor,
    ...data,
  })
}

function logChannelApi(
  channel: LogChannel,
  endpoint: string,
  params: Record<string, unknown>,
  result?: unknown,
  extra?: Record<string, unknown>,
) {
  logChannel(channel, 'API_CALL', {
    endpoint,
    params,
    result: summarizeApiResult(result),
    ...extra,
  })
}

type ChildSnapshotItem = {
  description?: string
  catalogID?: string
  visible?: boolean
  linkedID?: number
  idElementosEnlazados?: number
  textures?: unknown[]
}

function logChannelLinkedChildren(
  channel: LogChannel,
  label: string,
  parentId: string,
  getItem: (id: string) => ChildSnapshotItem | undefined,
  childIds: Iterable<string>,
) {
  const children = Array.from(childIds).map((childId) => {
    const child = getItem(childId)
    return {
      id: childId,
      description: child?.description,
      catalogID: child?.catalogID,
      visible: child?.visible,
      linkedID: child?.linkedID,
      idElementosEnlazados: child?.idElementosEnlazados,
      textures: Array.isArray(child?.textures)
        ? child.textures.map((t: any) => ({
            zoneName: t?.zoneName,
            zoneDisplayName: t?.zoneDisplayName,
            name: t?.name,
            acabado: t?.acabado,
            url: t?.url,
          }))
        : [],
    }
  })
  logChannel(channel, 'LINKED_CHILDREN', { label, parentId, children })
}

function logChannelItemTextures(
  channel: LogChannel,
  label: string,
  itemId: string,
  textures: Array<Record<string, unknown>> | undefined,
  catalogID?: string,
) {
  logChannel(channel, 'ITEM_TEXTURES', {
    label,
    itemId,
    catalogID,
    textures: (textures ?? []).map((t) => ({
      zoneName: t.zoneName,
      zoneDisplayName: t.zoneDisplayName,
      name: t.name,
      acabado: t.acabado,
      modelo: t.modelo,
      url: t.url,
      id: t.id,
    })),
  })
}

// --- Acabado ---

export function isSpecialAcabadoLabel(acabado: string): boolean {
  const upper = String(acabado || '')
    .trim()
    .toUpperCase()
  if (!upper) return false
  if (SPECIAL_ACABADO_LABELS.has(upper)) return true
  return /^(LARA|LINEAL|RUSTIK2?|LA|LI|RU|R2)\b/i.test(upper)
}

export function startAcabadoChangeTrace(context: {
  acabado: string
  modeEditor?: string
  itemId?: string
  catalogID?: string
  textureName?: string
  zoneName?: string
  zoneDisplayName?: string
}) {
  activeAcabadoTraceId = `acab-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
  setLastAcabadoModeEditor(context.modeEditor)
  logAcabado('TRACE_START', {
    ...context,
    isSpecialAcabado: isSpecialAcabadoLabel(context.acabado),
  })
  return activeAcabadoTraceId
}

export function endAcabadoChangeTrace(extra?: Record<string, unknown>) {
  logAcabado('TRACE_END', extra)
  activeAcabadoTraceId = null
}

export function getAcabadoChangeTraceId(): string | null {
  return activeAcabadoTraceId
}

export function logAcabado(step: string, data?: Record<string, unknown>) {
  logChannel('acabado', step, data)
}

export function logAcabadoApi(
  endpoint: string,
  params: Record<string, unknown>,
  result?: unknown,
  extra?: Record<string, unknown>,
) {
  logChannelApi('acabado', endpoint, params, result, extra)
}

export function logAcabadoTexture(
  action: string,
  texture: Record<string, unknown> | null | undefined,
  extra?: Record<string, unknown>,
) {
  if (!texture) {
    logAcabado('TEXTURE', { action, texture: null, ...extra })
    return
  }
  logAcabado('TEXTURE', {
    action,
    zoneName: texture.zoneName,
    zoneDisplayName: texture.zoneDisplayName,
    zone: texture.zone,
    name: texture.name,
    acabado: texture.acabado,
    modelo: texture.modelo,
    url: texture.url,
    id: texture.id,
    searchID: texture.searchID,
    groupID: texture.groupID,
    linkedTextureID: texture.linkedTextureID,
    ...extra,
  })
}

export function logLinkedChildrenSnapshot(
  label: string,
  parentId: string,
  getItem: (id: string) => ChildSnapshotItem | undefined,
  childIds: Iterable<string>,
) {
  logChannelLinkedChildren('acabado', label, parentId, getItem, childIds)
}

export function logItemTexturesSnapshot(
  label: string,
  itemId: string,
  textures: Array<Record<string, unknown>> | undefined,
  catalogID?: string,
) {
  logChannelItemTextures('acabado', label, itemId, textures, catalogID)
}

export function logAcabadoResolve(
  item: { catalogID?: string | null; idUnidades?: number | null },
  targetAcabado: string,
  decision: { strategy: string; idUnidades?: number; catalogId?: string },
  resolvedCatalogId?: string | null,
) {
  logAcabado('RESOLVE_TARGET', {
    fromCatalogID: item.catalogID,
    idUnidades: item.idUnidades,
    targetAcabado,
    isSpecialAcabado: isSpecialAcabadoLabel(targetAcabado),
    decision,
    resolvedCatalogId,
  })
}

// --- Gola ---

export function startGolaChangeTrace(context: {
  modeEditor?: string
  itemId?: string
  catalogID?: string
  isCurrentlyGola?: boolean
  targetGolaActive?: boolean
  idcambioarmazon?: number | null
  idcambiogolablanco?: number | null
  idcambiogolapetroleo?: number | null
}) {
  activeGolaTraceId = `gola-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
  logGola('TRACE_START', context)
  return activeGolaTraceId
}

export function endGolaChangeTrace(extra?: Record<string, unknown>) {
  logGola('TRACE_END', extra)
  activeGolaTraceId = null
}

export function getGolaChangeTraceId(): string | null {
  return activeGolaTraceId
}

export function logGola(step: string, data?: Record<string, unknown>) {
  logChannel('gola', step, data)
}

export function logGolaApi(
  endpoint: string,
  params: Record<string, unknown>,
  result?: unknown,
  extra?: Record<string, unknown>,
) {
  logChannelApi('gola', endpoint, params, result, extra)
}

export function logGolaLinkedChildrenSnapshot(
  label: string,
  parentId: string,
  getItem: (id: string) => ChildSnapshotItem | undefined,
  childIds: Iterable<string>,
) {
  logChannelLinkedChildren('gola', label, parentId, getItem, childIds)
}

export function logGolaItemTexturesSnapshot(
  label: string,
  itemId: string,
  textures: Array<Record<string, unknown>> | undefined,
  catalogID?: string,
) {
  logChannelItemTextures('gola', label, itemId, textures, catalogID)
}

export function logGolaResolve(
  item: {
    catalogID?: string | null
    idUnidades?: number | null
    idcambiogolablanco?: number | null
    idcambiogolapetroleo?: number | null
    idcambioarmazon?: number | null
  },
  decision: { strategy: string; idUnidades?: number; catalogId?: string },
  extra?: Record<string, unknown>,
) {
  const catalog = String(item.catalogID || '')
  const isPetroleo = catalog.startsWith('PE ')
  const hasGola = catalog.includes(' GO')

  logGola('RESOLVE_TARGET', {
    fromCatalogID: item.catalogID,
    idUnidades: item.idUnidades,
    hasGola,
    isPetroleo,
    idCambioArmazon: item.idcambioarmazon,
    idCambioGolaBlanco: item.idcambiogolablanco,
    idCambioGolaPetroleo: item.idcambiogolapetroleo,
    idUsedForToggle: hasGola
      ? item.idcambioarmazon
      : isPetroleo
        ? item.idcambiogolapetroleo
        : item.idcambiogolablanco,
    decision,
    ...extra,
  })
}
